import { Components } from "appworks/components/components";
import { CanvasService } from "appworks/graphics/canvas/canvas-service";
import { Orientation } from "appworks/graphics/canvas/orientation";
import { GraphicsService } from "appworks/graphics/graphics-service";
import { Layers } from "appworks/graphics/layers/layers";
import { Container } from "appworks/graphics/pixi/container";
import { Sprite } from "appworks/graphics/pixi/sprite";
import { Text } from "appworks/graphics/pixi/text";
import { TextPosition } from "appworks/graphics/pixi/text-position";
import { Result } from "appworks/model/gameplay/records/results/result";
import { Services } from "appworks/services/services";
import { TranslationsService } from "appworks/services/translations/translations-service";
import { Contract } from "appworks/utils/contracts/contract";
import { FooterComponent, FooterComponentConfig } from "slotworks/components/footer/footer-component";
import { AbstractMatrixComponent } from "slotworks/components/matrix/abstract-matrix-component";
import { LineResult } from "slotworks/model/gameplay/records/results/line-result";
import { SymbolWinResult } from "slotworks/model/gameplay/records/results/symbol-win-result";
import { WaysResult } from "slotworks/model/gameplay/records/results/ways-result";
import { slotDefinition } from "slotworks/model/slot-definition";
export interface SymbolFooterComponentConfig extends FooterComponentConfig {
    layer?: Layers;
    // Optional; pass this through to currency format whenever it is used in this component
    forceCreditMode?: boolean;

    // The scale of the footer symbols, relative to the height of the footer text.
    symbolScale: number;
    // X padding for symbols (at percentage of symbol width) default 0.5
    paddingX: number;
    // Y padding for symbols (at percentage of symbol height) default 0.5
    paddingY: number;
    // Only show 1 symbol to represent a win instead of all symbols involved
    singleSymbol: boolean;
    // String list of ids which can be displayed - if not set will use all symbols in game definition
    validSymbols?: string[];
    // String list of ids which can be displayed
    invalidSymbols?: string[];
}


/**
 * This component will prepend the footer messages with the appropriate number
 * of winning symbols.
 *
 * In order for this to initalise correctly, it's required that a smaller symbol
 * graphic exists in the statics folder with the filename:
 * `${symbolId}_small.png`
 *
 * If no such graphics exist, this component will error out.
 */
export class SymbolFooterComponent extends FooterComponent {

    protected numberOfReels: number;
    protected symbolWidth: number;
    protected symbolFooterY: number;

    protected symbols: Sprite[] = [];
    protected footerText: Text;
    protected container: Container;
    protected currentResult: SymbolWinResult;

    protected footerSymbolScales = new Map<Orientation, number>();
    private originalTextPositions = new Map<Orientation, TextPosition>();

    protected orientationMethod?: Function;

    protected config: SymbolFooterComponentConfig = {
        symbolScale: 1.2,
        paddingX: 0.5,
        paddingY: 0.5,
        singleSymbol: false
    };

    constructor(config?: Partial<SymbolFooterComponentConfig>) {
        super(config);

        if (config) {
            this.config = { ...this.config, ...config };
        }

        this.setup();

        this.layer.onSceneEnter.add(() => this.setup());
    }

    public setup() {
        this.numberOfReels = Components.get(AbstractMatrixComponent).getReels().length;
        this.footerText = this.layer.getText("value") as Text;
        this.container = new Container();

        // this.createSymbolSprites();

        this.setupFooterSymbols(Orientation.LANDSCAPE);
        this.setupFooterSymbols(Orientation.PORTRAIT);

        /**
         * This is necessary because once we add the images to the text position, we may have to center the text.
         * Doing this requires that we know how much space the translated text consumes, calculating this is only possible
         * for the currently active orientation.
         */
        if (this.orientationMethod !== undefined) {
            Services.get(CanvasService).onOrientationChange.remove(this.orientationMethod);
        }
        this.orientationMethod = (orientation: Orientation) => this.repositionText(orientation);
        Services.get(CanvasService).onOrientationChange.add(this.orientationMethod);
    }
    
    public showWaysWin(result: WaysResult, symbolId: string) {
        super.showWaysWin(result, symbolId);
        // this.showSymbolsForResult(result);
    }
    public showMiscWin(textId: string, result?: Result) {
        this.setText(Services.get(TranslationsService).get(textId));
        if (result && result instanceof SymbolWinResult) {
            // this.showSymbolsForResult(result);
        }
    }

    public showLineWin(result: LineResult, symbolId: string) {
        super.showLineWin(result/* , symbolId */).execute();
        // this.showSymbolsForResult(result);

        return Contract.empty();
    }

    protected getShrinkRatio(orientation: Orientation) {
        const tallestSymbolHeight = this.getTallestSymbolHeight();

        const footerTextHeight = this.footerText.getPosition(orientation).height * this.config.symbolScale;
        const shrinkRatio = footerTextHeight / tallestSymbolHeight;
        return shrinkRatio;
    }

    protected setText(text: string) {
        super.setText(text);

        if (this.currentResult) {
            this.currentResult = null;
        }

        if (!this.footerText) {
            return;
        }

        this.hideSymbols();

        this.restoreOriginalTextPosition(Orientation.LANDSCAPE);
        this.restoreOriginalTextPosition(Orientation.PORTRAIT);
    }

    protected repositionText(orientation: Orientation) {
        if (!this.currentResult) {
            return;
        }

        const originalTextPosition = this.originalTextPositions.get(orientation);
        const footerText = this.footerText.getPosition(orientation);

        footerText.x = originalTextPosition.x;
        footerText.y = originalTextPosition.y;

        const numberOfSymbols = this.config.singleSymbol ? 1 : this.currentResult.matches;
        const symbolSpaceConsumed = numberOfSymbols * this.symbolWidth * this.footerSymbolScales.get(orientation);
        const availableTextWidth = originalTextPosition.width - symbolSpaceConsumed;

        footerText.width = availableTextWidth;
        this.footerText.forceAutoResize();

        const textWidth = this.footerText.width;
        const spaceNeeded = symbolSpaceConsumed + textWidth;
        const unusedSpace = originalTextPosition.width - spaceNeeded;

        const containerPosition = this.container.getPosition(orientation);
        containerPosition.x = 10 + originalTextPosition.x + (unusedSpace / 2);
        footerText.x = containerPosition.x + symbolSpaceConsumed - 5;

        footerText.align = "left";
    }

    protected restoreOriginalTextPosition(orientation: Orientation) {

        const originalTextPosition = this.originalTextPositions.get(orientation);
        const footerText = this.footerText.getPosition(orientation);

        footerText.align = originalTextPosition.align;
        footerText.x = originalTextPosition.x;
        footerText.y = originalTextPosition.y;
        footerText.width = originalTextPosition.width;
        footerText.height = originalTextPosition.height;
    }

    protected hideSymbols() {
        this.container.children.forEach((child) => {
            child.visible = false;
        });
    }

    // protected showSymbolsForResult(result: SymbolWinResult) {

    //     this.currentResult = result;

    //     for (let i = 0; i < this.numberOfReels; i++) {
    //         const symbol = this.symbols[i];

    //         if (i >= result.matches || (this.config.singleSymbol && i > 0)) {
    //             symbol.visible = false;
    //             continue;
    //         }

    //         symbol.visible = true;
    //         const id = this.config.singleSymbol ? result.symbolType : (result.symbols[i] || result.symbolType);
    //         const texture = Services.get(GraphicsService).getTextureWithFallback([`footer_${id}`, id]);
    //         symbol.texture = texture;
    //     }

    //     this.repositionText(Orientation.LANDSCAPE);
    //     this.repositionText(Orientation.PORTRAIT);
    // }

    // protected createSymbolSprites() {

    //     const reels = Components.get(AbstractMatrixComponent).getReels();

    //     this.symbols = reels.map(() => {

    //         const id = this.getSymbolList()[0];
    //         const symbol = Services.get(GraphicsService).createSpriteWithFallback([`footer_${id}`, id]);

    //         symbol.anchor.set(0.5);
    //         symbol.visible = false;

    //         this.container.addChild(symbol);

    //         return symbol;
    //     });
    // }

    protected setupFooterSymbols(orientation: Orientation) {

        this.footerSymbolScales.set(orientation, this.getShrinkRatio(orientation));
        this.originalTextPositions.set(orientation, this.footerText.getPosition(orientation).clone());

        // const footerSymbolScale = this.footerSymbolScales.get(orientation);

        // this.symbols.forEach((symbol) => {
        //     const symbolScale = symbol.getPosition(orientation).scale;
        //     symbolScale.x = footerSymbolScale;
        //     symbolScale.y = footerSymbolScale;
        // });

        // const spacePerSymbol = this.getWidestSymbolWidth();

        // this.symbolWidth = /* spacePerSymbol * (1 + this.config.paddingX); */0;
        // this.symbolFooterY = spacePerSymbol * (1 + this.config.paddingY);

        this.layer.add(this.container);
        this.positionFooterSymbols(orientation);
    }

    protected positionFooterSymbols(orientation: Orientation) {

        // const footerSymbolScale = this.footerSymbolScales.get(orientation);

        // this.symbols.forEach((symbol, index) => {
        //     const symbolPosition = symbol.getPosition(orientation);
        //     symbolPosition.x = this.symbolWidth * index * footerSymbolScale;
        //     symbolPosition.y = this.symbolFooterY * footerSymbolScale;

        //     // symbol.updateTransform();
        // });

        const footerPosition = this.footerText.getPosition(orientation);
        const containerPosition = this.container.getPosition(orientation);

        const centerOfFooter = footerPosition.x - footerPosition.width / 2;
        containerPosition.x = 30 + centerOfFooter;
        containerPosition.y = footerPosition.y;
    }

    protected getTallestSymbolHeight() {
        const textureHeights = this.getSymbolList().map((id) => {
            return Services.get(GraphicsService).getTextureWithFallback([`footer_${id}`, id]).height;
        });

        return Math.max(...textureHeights);
    }

    protected getWidestSymbolWidth() {
        const textureWidths = this.getSymbolList().map((id) => {
            return Services.get(GraphicsService).getTextureWithFallback([`footer_${id}`, id]).width;
        });

        return Math.max(...textureWidths);
    }

    protected getSymbolList() {

        if (this.config.validSymbols) {
            return this.config.validSymbols;
        }

        const allSymbols = slotDefinition.symbolDefinitions.map((symbol) => symbol.id);

        if (this.config.invalidSymbols) {
            return allSymbols.filter((symbol) => this.config.invalidSymbols.every((invalidSymbol) => symbol !== invalidSymbol));
        }

        return allSymbols;
    }
}
